import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import TravelTripsAPI from 'dashboard/api/travel/trips';

/**
 * The two things an agent can do to a trip without leaving the screen they are on: ask the
 * airline what it says now, and clear a schedule-change alarm.
 *
 * Shared between the sidebar panel and the trip detail dialog so acknowledgement behaves the
 * same in both. The 409 branch is the reason this is not two one-line API calls: a
 * conflict means the itinerary moved AGAIN between rendering and clicking, and the correct
 * response is to keep the alarm up and show the newer snapshot - not to swallow the write.
 * Getting that wrong in one of two copies is how a cancelled segment goes unnoticed.
 *
 * @returns {Object} refreshingId, acknowledgingId, refresh(trip), acknowledge(trip).
 *   Both actions resolve to the updated trip, or null when nothing was applied.
 */
export function useTripActions() {
  const { t } = useI18n();

  const refreshingId = ref(null);
  const acknowledgingId = ref(null);

  const refresh = async trip => {
    refreshingId.value = trip.id;
    try {
      const { data } = await TravelTripsAPI.refresh(trip.id);
      return data.trip;
    } catch (error) {
      useAlert(
        error?.response?.data?.error || t('TRAVEL.TRIP_PANEL.ERROR.REFRESH')
      );
      return null;
    } finally {
      refreshingId.value = null;
    }
  };

  // The digest names the snapshot the agent actually read, so the server can refuse to
  // clear an alarm for a change nobody has seen.
  const acknowledge = async trip => {
    acknowledgingId.value = trip.id;
    try {
      const { data } = await TravelTripsAPI.acknowledgeScheduleChange(
        trip.id,
        trip.provider_segment_digest
      );
      return data.trip;
    } catch (error) {
      if (error?.response?.status === 409) {
        useAlert(t('TRAVEL.TRIP_PANEL.ERROR.MOVED_AGAIN'));
        // The conflict body carries the trip as it is now, so the caller shows the change
        // the agent has not read yet rather than the one they thought they were clearing.
        return error.response.data.trip;
      }
      useAlert(
        error?.response?.data?.error || t('TRAVEL.TRIP_PANEL.ERROR.ACKNOWLEDGE')
      );
      return null;
    } finally {
      acknowledgingId.value = null;
    }
  };

  return { refreshingId, acknowledgingId, refresh, acknowledge };
}
